import React, { useState } from 'react'
import { TaskInlineInput } from '../task-inlineinput/TaskInlineInput'
import { useTaskListContext } from './Context'
import { TaskStatusDropdown } from '../TaskStatus/TaskStatusDropdown'
import { TaskMembersInlineInput } from '../task-inlineinput/TaskMembersInlineInput'
import { TaskTimeSpentInlineInput } from '../task-inlineinput/TaskTimeSpentInlineInput'
import {
  CheckboxBodyCell,
  ExtendingRow,
  type RowProps,
  TableCellDragHandle,
  selectedRowClassName,
} from '@ztechapps/webui.data-tools'
import type { EntityWithSelection } from '@ztechapps/common.hook'
import { useTheme } from '@ztechapps/common.hook'
import { RowAction } from './row-action'
import { TaskDescriptionInlineInput } from '../task-inlineinput/TaskDescriptionInlineInput'
import { Col, Nav, Row, Stack, Tab, TabPane } from 'react-bootstrap'
import { TagsInlineInput } from '@ztechapps/webui.input'
import type { Task } from '@ztechapps/common.contract'
import { ShowIf } from '@ztechapps/webui.util'
import { TaskEndDateInlineInput, TaskStartDateInlineInput } from '../task-inlineinput/TaskDateInlineInput'
import { SubTaskList } from './sub-tasks/SubTaskList'
import { TaskStatusActionsTab } from '../TaskStatusActions/TaskStatusActionsTab'
import { TaskAssets } from '../TaskAssets'
export type TaskListRowProps = RowProps & EntityWithSelection<Task>

export const TaskListRow = ({ ...props }: TaskListRowProps) => {
  const { taskListWithSelection } = useTaskListContext()
  const { toggleSelectBy } = taskListWithSelection

  const [showActionButtons, setShowActionButtons] = useState(false)
  const { _id, _selected } = props
  const { updateConfig, afterUpdate, disabled, enableReorder } = useTaskListContext()
  const initialValues = { _id, ...props }

  const { query, updateParams } = updateConfig
  const mutationConfig = { query }

  const { colorMode } = useTheme()

  return (
    <ExtendingRow
      extensionRowClassName={colorMode.isDark ? 'table-dark' : 'table-light'}
      rowClassName={_selected ? selectedRowClassName : ''}
      extensionRow={<ExtensionRow {...props} />}
      {...props}
      onMouseOver={() => setShowActionButtons(true)}
      onMouseLeave={() => setShowActionButtons(false)}
    >
      <ShowIf condition={!disabled}>
        <CheckboxBodyCell
          className="mx-0 px-0"
          checked={_selected}
          onChange={() => {
            toggleSelectBy((item) => item._id === _id)
          }}
        />
        <ShowIf condition={!!enableReorder}>
          <TableCellDragHandle />
        </ShowIf>
      </ShowIf>

      <td>
        <TaskInlineInput
          mutationConfig={mutationConfig}
          updateParams={updateParams}
          afterUpdate={afterUpdate}
          {...{
            canEdit: !disabled,
            initialValues,
            name: 'name',
          }}
        />
      </td>

      <td className="p-0 m-0 align-middle">
        <TaskStatusDropdown
          disabled={disabled}
          taskId={_id}
          value={props?.status}
          onChange={async (e) => {
            await afterUpdate?.(e)
          }}
        />
      </td>
      <td>
        <TaskInlineInput
          mutationConfig={mutationConfig}
          updateParams={updateParams}
          afterUpdate={afterUpdate}
          parentInputComponent={TaskStartDateInlineInput}
          canEdit={!disabled}
          initialValues={initialValues}
          name="startDate"
          label=""
        />
      </td>
      <td>
        <TaskInlineInput
          mutationConfig={mutationConfig}
          updateParams={updateParams}
          afterUpdate={afterUpdate}
          parentInputComponent={TaskEndDateInlineInput}
          canEdit={!disabled}
          initialValues={initialValues}
          name="endDate"
          label=""
        />
      </td>
      <td>
        <TaskInlineInput
          mutationConfig={mutationConfig}
          updateParams={updateParams}
          afterUpdate={afterUpdate}
          initialValues={initialValues}
          canEdit={!disabled}
          parentInputComponent={TaskTimeSpentInlineInput}
          name="timeSpent"
          label=""
        />
      </td>
      <td className="p-0 m-0 align-middle">
        <TaskInlineInput
          mutationConfig={mutationConfig}
          updateParams={updateParams}
          afterUpdate={afterUpdate}
          initialValues={initialValues}
          canEdit={!disabled}
          parentInputComponent={TaskMembersInlineInput}
          name="members"
          label=""
        />
      </td>

      <RowAction showActionButtons={showActionButtons} task={props} />
    </ExtendingRow>
  )
}

export const ExtensionRow = ({ _id, description, remarksActivity, tags, statusActions, assets }: RowProps) => {
  const { updateConfig, afterUpdate, disabled, components, subTaskConfig } = useTaskListContext()
  const initialValues = { _id, tags }
  const { query, updateParams } = updateConfig
  const mutationConfig = { query }

  const hasSubTaskConfig = subTaskConfig?.enabled
  const [activeTab, setActiveTab] = React.useState('description')

  return (
    <Tab.Container defaultActiveKey="description" onSelect={(e) => setActiveTab(e)}>
      <Nav variant="underline">
        <Nav.Item>
          <Nav.Link eventKey="description">Description</Nav.Link>
        </Nav.Item>
        <ShowIf condition={hasSubTaskConfig}>
          <Nav.Item>
            <Nav.Link eventKey="subTasks">Sub Tasks</Nav.Link>
          </Nav.Item>
        </ShowIf>
        <Nav.Item>
          <Nav.Link eventKey="assets">Assets</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="statusActions">Status Actions</Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="description" className="pt-4">
          <Row className="g-3">
            <Col md={8} className="border-end">
              <div className="mb-5 ms-4">
                <TaskDescriptionInlineInput
                  initialValues={{
                    _id,
                    description,
                  }}
                />
              </div>
            </Col>
            <Col md={4}>
              <Stack gap={3}>
                <components.remarksActivity remarksActivity={remarksActivity} _id={_id} />

                <TaskInlineInput
                  mutationConfig={mutationConfig}
                  updateParams={updateParams}
                  afterUpdate={afterUpdate}
                  label="Tags"
                  {...{
                    canEdit: !disabled,
                    initialValues,
                    name: 'tags',
                    parentInputComponent: TagsInlineInput,
                    afterUpdate: afterUpdate,
                  }}
                />
              </Stack>
            </Col>
          </Row>
        </Tab.Pane>
        {/*This effetively mount and unmount the SubTaskList*/}
        <ShowIf condition={hasSubTaskConfig && activeTab === 'subTasks'}>
          <TabPane eventKey="subTasks">
            <SubTaskList taskId={_id} level={subTaskConfig?.level} />
          </TabPane>
        </ShowIf>
        <TabPane eventKey="statusActions">
          <div className="pt-3">
            <TaskStatusActionsTab
              taskId={_id}
              statusActions={statusActions}
              canEdit={!disabled}
              onUpdated={afterUpdate}
            />
          </div>
        </TabPane>
        <TabPane eventKey="assets">
          <div className="pt-3">
            <TaskAssets entityId={_id} assets={assets} canEdit={!disabled} />
          </div>
        </TabPane>
      </Tab.Content>
    </Tab.Container>
  )
}
